import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Badge, Button, Modal, Input, Select, FormStack, Alert } from "@marekl/adminer/components";
import {
  useCreateUser,
  useResetUserPassword,
  useUpdateUser,
  type CreateUserPayload,
  type Role,
  type UpdateUserPayload,
  type User,
} from "../../auth/queries";
import { generateSecurePassword } from "@marekl/adminer/util";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import styles from "./UsersPage.module.css";

export function RoleBadge({ role }: { role: Role }) {
  const variants: Record<Role, "info" | "warn" | "err"> = {
    reception: "info",
    manager: "warn",
    superadmin: "err",
  };
  const labels: Record<Role, string> = {
    reception: "Recepce",
    manager: "Správce",
    superadmin: "Superadmin",
  };
  return <Badge variant={variants[role]}>{labels[role]}</Badge>;
}

export function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createUser = useCreateUser();
  const [form, setForm] = useState<CreateUserPayload>({
    username: "",
    display_name: "",
    password: "",
    role: "reception",
    email: "",
    password_locked: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateUserPayload, string>>>({});

  function validate() {
    const e: typeof errors = {};
    if (!form.username.trim()) e.username = "Povinné pole";
    if (form.password.length < 8) e.password = "Heslo musí mít alespoň 8 znaků";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    try {
      await createUser.mutateAsync(form);
      notifySuccess("Uživatel byl vytvořen.");
      setForm({ username: "", display_name: "", password: "", role: "reception", email: "", password_locked: false });
      onClose();
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Modal
      open={open}
      title="Přidat uživatele"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Zrušit</Button>
          <Button variant="primary" loading={createUser.isPending} onClick={handleSubmit as unknown as React.MouseEventHandler}>
            Vytvořit
          </Button>
        </>
      }
    >
      <FormStack as="form" onSubmit={handleSubmit}>
        <Input
          id="new-username"
          label="Uživatelské jméno *"
          value={form.username}
          onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          error={errors.username}
          autoComplete="off"
        />
        <Input
          id="new-display-name"
          label="Jméno"
          value={form.display_name}
          onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
        />
        <Input
          id="new-email"
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <Select
          label="Role"
          id="new-role"
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
        >
          <option value="reception">Recepce</option>
          <option value="manager">Správce</option>
          <option value="superadmin">Superadmin</option>
        </Select>
        <Input
          id="new-password"
          label="Heslo *"
          type="text"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          error={errors.password}
          autoComplete="new-password"
          spellCheck={false}
          suffix={
            <button
              type="button"
              className={styles.generateBtn}
              onClick={() => setForm((f) => ({ ...f, password: generateSecurePassword() }))}
              title="Vygenerovat bezpečné heslo"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Generovat
            </button>
          }
        />
        <label className={styles.checkLabel}>
          <input
            type="checkbox"
            checked={form.password_locked}
            onChange={(e) => setForm((f) => ({ ...f, password_locked: e.target.checked }))}
          />
          Zablokovat změnu hesla
        </label>
      </FormStack>
    </Modal>
  );
}

export function EditUserModal({ user, onClose }: { user: User; onClose: () => void }) {
  const updateUser = useUpdateUser(user.id);
  const [form, setForm] = useState<UpdateUserPayload & { display_name: string; email: string; role: Role }>({
    display_name: user.display_name ?? "",
    role: user.role,
    email: user.email ?? "",
    active: user.active,
    password_locked: user.password_locked,
  });

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    try {
      await updateUser.mutateAsync(form);
      notifySuccess("Uživatel byl upraven.");
      onClose();
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Modal open title={`Upravit: ${user.username}`} onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Zrušit</Button>
          <Button variant="primary" loading={updateUser.isPending} onClick={handleSubmit as unknown as React.MouseEventHandler}>
            Uložit
          </Button>
        </>
      }
    >
      <FormStack as="form" onSubmit={handleSubmit}>
        <Input
          id="edit-display-name"
          label="Jméno"
          value={form.display_name}
          onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
        />
        <Input
          id="edit-email"
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <Select
          label="Role"
          id="edit-role"
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
        >
          <option value="reception">Recepce</option>
          <option value="manager">Správce</option>
          <option value="superadmin">Superadmin</option>
        </Select>
        <div className={styles.checkGroup}>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            Aktivní
          </label>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={form.password_locked}
              onChange={(e) => setForm((f) => ({ ...f, password_locked: e.target.checked }))}
            />
            Zablokovat změnu hesla
          </label>
        </div>
      </FormStack>
    </Modal>
  );
}

export function ResetPasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
  const resetPw = useResetUserPassword(user.id);
  const [newPw, setNewPw] = useState("");
  const [error, setError] = useState("");

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (newPw.length < 8) {
      setError("Heslo musí mít alespoň 8 znaků");
      return;
    }
    try {
      await resetPw.mutateAsync({ new_password: newPw });
      notifySuccess("Heslo bylo resetováno.");
      setNewPw("");
      onClose();
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Modal open title={`Resetovat heslo: ${user.username}`} onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Zrušit</Button>
          <Button variant="primary" loading={resetPw.isPending} onClick={handleSubmit as unknown as React.MouseEventHandler}>
            Nastavit heslo
          </Button>
        </>
      }
    >
      <FormStack as="form" onSubmit={handleSubmit}>
        <Alert variant="info">
          Nové heslo nastaví superadmin bez potřeby znát aktuální heslo uživatele.
        </Alert>
        <Input
          id="reset-pw"
          label="Nové heslo *"
          type="text"
          value={newPw}
          onChange={(e) => { setNewPw(e.target.value); setError(""); }}
          error={error}
          autoComplete="new-password"
          spellCheck={false}
          suffix={
            <button
              type="button"
              className={styles.generateBtn}
              onClick={() => { setNewPw(generateSecurePassword()); setError(""); }}
              title="Vygenerovat bezpečné heslo"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Generovat
            </button>
          }
        />
      </FormStack>
    </Modal>
  );
}
